<template>
  <article id="moonday-content" class="md-page" tabindex="-1">
    <section class="md-hero">
      <div class="container-wrapper md-hero-grid">
        <div class="md-hero-copy">
          <router-link to="/games" class="md-back"
            >← Games &amp; Apps</router-link
          >
          <div class="md-brand">
            <img
              :src="assets + 'icon-96.webp'"
              :srcset="assets + 'icon-192.webp 2x'"
              width="64"
              height="64"
              alt="MoonDay crescent app icon"
            /><span>MoonDay <small>AN APP BY ARI.company</small></span>
          </div>
          <p class="md-eyebrow">PERSONAL DIARY · ANDROID · 18+</p>
          <h1>Your days,<br /><span>your way.</span></h1>
          <p class="md-lead">
            A quiet place to record periods and intimacy. Keep your own history,
            return to it when you need to, and choose what you track.
          </p>
          <div class="md-actions">
            <a href="#inside-moonday" class="md-button"
              >Explore the app <span aria-hidden="true">↗</span></a
            ><a
              href="#moonday-availability"
              class="md-button md-button-secondary"
              >Availability</a
            >
          </div>
          <p class="md-status">
            <span aria-hidden="true"></span> In internal testing · No public
            download yet
          </p>
          <p class="md-hero-note">
            The current test is a local diary. Accounts, cloud sync and cloud
            recovery are not available.
          </p>
        </div>
        <figure class="md-hero-visual">
          <div class="md-phone">
            <img
              :src="assets + '01-today-en-800.webp'"
              :srcset="
                assets +
                '01-today-en-480.webp 480w, ' +
                assets +
                '01-today-en-800.webp 800w'
              "
              sizes="(max-width: 600px) 280px, 300px"
              width="1080"
              height="2400"
              alt="MoonDay Today screen with quick entry buttons, a fictional diary entry and On this phone status"
              fetchpriority="high"
            />
          </div>
          <figcaption>Actual app · Fictional sample entries</figcaption>
        </figure>
      </div>
    </section>

    <section class="md-section md-intro">
      <div class="container-wrapper">
        <p class="md-eyebrow">A RECORD THAT BELONGS TO YOUR EVERYDAY LIFE</p>
        <h2>Notice your history.<br />Without targets or predictions.</h2>
        <div class="md-benefits">
          <div>
            <span class="md-number">01</span>
            <h3>Choose what matters</h3>
            <p>
              Track periods, intimacy or both. Hide a category whenever you
              want; its history stays until you choose to delete it.
            </p>
          </div>
          <div>
            <span class="md-number">02</span>
            <h3>Make room for detail</h3>
            <p>
              Record dates, intimacy counts, period start and end dates, daily
              flow and optional notes. Edit an entry when something changes.
            </p>
          </div>
          <div>
            <span class="md-number">03</span>
            <h3>Look back clearly</h3>
            <p>
              Move through your calendar and review totals for this month, last
              month or a date range you choose.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="inside-moonday" class="md-section md-walkthrough">
      <div class="container-wrapper md-walk-grid">
        <div class="md-walk-copy">
          <p class="md-eyebrow">INSIDE MOONDAY</p>
          <h2>Small moments.<br />A useful record.</h2>
          <p>
            Explore real screens from the app. All entries shown here were
            created for demonstration.
          </p>
          <div
            class="md-screen-choices"
            role="group"
            aria-label="Choose an app screenshot"
          >
            <button
              v-for="(screen, i) in screens"
              :key="screen.id"
              type="button"
              :aria-pressed="selected === i ? 'true' : 'false'"
              aria-controls="md-screen-detail"
              @click="selected = i"
            >
              <span class="md-choice-number">0{{ i + 1 }}</span
              >{{ screen.label }}<span aria-hidden="true">↗</span>
            </button>
          </div>
          <div
            id="md-screen-detail"
            class="md-screen-detail"
            aria-live="polite"
            aria-atomic="true"
          >
            <h3>{{ active.title }}</h3>
            <p>{{ active.copy }}</p>
          </div>
        </div>
        <figure class="md-walk-image">
          <div class="md-phone">
            <img
              :key="active.id"
              :src="assets + active.id + '-800.webp'"
              :srcset="
                assets +
                active.id +
                '-480.webp 480w, ' +
                assets +
                active.id +
                '-800.webp 800w'
              "
              sizes="(max-width: 600px) calc(100vw - 64px), 340px"
              width="1080"
              height="2400"
              :alt="active.alt"
              loading="lazy"
            />
          </div>
          <figcaption>
            {{ active.label }} · Fictional sample entries<br /><a
              :href="assets + active.id + '-800.webp'"
              target="_blank"
              rel="noopener"
              >Open full-size screenshot
              <span class="md-sr-only">in a new tab</span> ↗</a
            >
          </figcaption>
        </figure>
      </div>
    </section>

    <section class="md-section md-control">
      <div class="container-wrapper md-control-grid">
        <div>
          <p class="md-eyebrow">PRIVACY, IN PRACTICAL TERMS</p>
          <h2>Your diary.<br />Deliberate choices.</h2>
          <p class="md-lead">
            The current Android test stores diary records in an encrypted
            database on your phone.
          </p>
          <router-link v-if="reviewDrafts" to="/games/moonday/privacy-policy" class="md-text-link"
            >Read the privacy review draft →</router-link
          >
        </div>
        <div class="md-controls">
          <section>
            <h3>A little more privacy on your phone</h3>
            <p>
              Enable app lock with your device’s authentication. The production
              app blocks screenshots where Android supports it and masks its
              content when you leave. These controls cannot protect a
              compromised device.
            </p>
          </section>
          <section>
            <h3>Reminders, only if you choose</h3>
            <p>
              Local reminders are off by default. If enabled, they use generic
              wording without diary details. Choose English or Ukrainian and a
              light or dark theme.
            </p>
          </section>
          <section>
            <h3>Export with care. Delete with clarity.</h3>
            <p>
              Save a selected date range as JSON or CSV through Android’s
              document picker. Exports are not encrypted by MoonDay. You can
              delete individual entries or clear the diary; copies you saved
              elsewhere need separate removal.
            </p>
            <router-link v-if="reviewDrafts" to="/games/moonday/delete-data" class="md-text-link"
              >How to delete diary data →</router-link
            >
          </section>
        </div>
      </div>
    </section>

    <section id="moonday-availability" class="md-section md-availability">
      <div class="container-wrapper md-availability-grid">
        <div>
          <p class="md-eyebrow">WHERE WE ARE TODAY</p>
          <h2>A careful first step.</h2>
          <p>
            MoonDay 1.0.0 is available to its invited internal Android testers.
            General downloads and public tester registration are not open.
          </p>
          <p>
            Android 7.0 or later · Adults 18+ by self-declaration<br />English
            and Ukrainian · No ads or subscriptions in this build
          </p>
        </div>
        <aside>
          <span class="md-pill">INTERNAL TEST</span>
          <h3>Local diary available</h3>
          <p>
            Accounts, synchronization and cloud recovery remain in development.
            There is no confirmed public release date or iOS release.
          </p>
          <p class="md-note">
            Existing testers: use your private invitation and fictional entries
            while testing. That invitation is not a public download link.
          </p>
        </aside>
      </div>
    </section>

    <section class="md-section md-faq">
      <div class="container-wrapper md-faq-grid">
        <div>
          <p class="md-eyebrow">A FEW USEFUL ANSWERS</p>
          <h2>Before you begin.</h2>
        </div>
        <div>
          <details>
            <summary>Does MoonDay predict periods or fertility?</summary>
            <p>
              No. It shows what you recorded, including completed period lengths
              and intervals between recorded starts when there is enough
              history. Missing entries can change those summaries. MoonDay does
              not predict ovulation, fertility, pregnancy or “safe days”, and is
              not a medical or contraceptive tool.
            </p>
          </details>
          <details>
            <summary>Do I need an account or internet connection?</summary>
            <p>
              The current test does not need an account to keep a local diary.
              You can add, edit and review entries offline. Account creation and
              cloud sync are unavailable; a setting for cloud access does not
              make the service operational.
            </p>
          </details>
          <details>
            <summary>Will my diary return on a new phone?</summary>
            <p>
              Cloud recovery is unavailable. The app is configured to exclude
              its private data from Android backup and device transfer. Losing
              or clearing the app may lose the diary. You may export a copy, but
              importing exported files back into MoonDay is not implemented.
            </p>
          </details>
          <details>
            <summary>Can I use only one type of tracking?</summary>
            <p>
              Yes. Period and intimacy tracking have separate switches.
              Switching one off hides that category without deleting its
              records. Notes remain available.
            </p>
          </details>
          <details>
            <summary>
              How do I get help without sharing private entries?
            </summary>
            <p>
              <span v-if="reviewDrafts">Start with the
              <router-link to="/games/moonday/delete-data"
                >diary-data help page</router-link
              >.</span> Email
              <a
                href="mailto:privacy@ari.company?subject=MoonDay%20support%20request"
                >privacy@ari.company</a
              >, monitored by Lidiia Dankevych. Describe the app version, screen
              and error; do not send diary contents, exports, passwords or
              personal health information.
            </p>
          </details>
        </div>
      </div>
    </section>
    <nav
      class="md-product-links container-wrapper"
      aria-label="MoonDay information"
    >
      <a href="mailto:privacy@ari.company?subject=MoonDay%20support%20request"
        >MoonDay support</a
      >
      <router-link v-if="reviewDrafts" to="/games/moonday/privacy-policy"
        >Privacy Policy · review draft</router-link
      ><router-link v-if="reviewDrafts" to="/games/moonday/delete-data"
        >Delete diary data &amp; help</router-link
      ><router-link to="/games">All Games &amp; Apps</router-link>
    </nav>
  </article>
</template>
<script>
import { productMeta } from "./product-meta";
export default {
  name: "MoonDayPage",
  data: () => ({
    reviewDrafts: process.env.VUE_APP_INCLUDE_REVIEW_DRAFTS === "true",
    assets: "/games/moonday/release-1/",
    selected: 1,
    screens: [
      {
        id: "01-today-en",
        label: "Today",
        title: "Start with a moment.",
        copy: "Quick entry buttons lead straight to a period or intimacy record. Today’s entries stay together, with a clear On this phone storage status.",
        alt: "Actual MoonDay Today screen showing quick entry controls and a fictional intimacy record",
      },
      {
        id: "02-calendar-en",
        label: "Calendar",
        title: "Find a day. See what you recorded.",
        copy: "Browse months, jump to today and select a date to review or edit entries. Distinct heart, droplet and note markers add meaning beyond colour. An ongoing period is distinguished from a completed range.",
        alt: "Actual MoonDay calendar with fictional October entries, heart and droplet markers, a legend and selected day",
      },
      {
        id: "03-add-entry-en",
        label: "Add an entry",
        title: "Only the details you want.",
        copy: "Choose a date and record an intimacy count, a period, daily flow or a note. Optional time and notes add context. Actual-event dates are today or in the past.",
        alt: "MoonDay real Add entry screen with entry type, date, count, details and Save entry controls",
      },
      {
        id: "04-insights-en",
        label: "History",
        title: "Look back, without a forecast.",
        copy: "Choose this month, last month or a custom range. Totals count recorded occasions, not just entries. Completed period lengths and intervals describe your recorded history; they do not predict what comes next.",
        alt: "MoonDay history screen with date filters and summaries calculated from fictional entries",
      },
      {
        id: "05-settings-en",
        label: "Settings",
        title: "Make the app comfortable for you.",
        copy: "Choose tracking categories, app lock and discreet reminders. Language, theme, week start, export and clearing the diary are further down the Settings screen. Cloud access is unavailable in this test.",
        alt: "Actual MoonDay Settings screen showing local storage status, app lock and reminder controls; cloud service is unavailable",
      },
    ],
  }),
  computed: {
    active() {
      return this.screens[this.selected];
    },
  },
  metaInfo() {
    return productMeta(
      "MoonDay — Your days, your way | ARI.company",
      "A personal diary for periods and intimacy, with a calendar, recorded history and encrypted local storage. Android internal test; public downloads are not open.",
      "/games/moonday/release-1/social.jpg",
    );
  },
};
</script>
<style src="./moonday.css"></style>
